import { CommonModule } from '@angular/common';
import {
    Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Observable } from 'rxjs';

import { ConnectionControllerService, ConnectionSummaryDto } from '@nucleus/connector-admin-ui';
import { RequestHandler } from '../../../services/request-handler';

/**
 * Connection picker — the bridge that lets a capability's Connector field
 * ({@code x-options: { source:'connector-catalog', connectorType:'<type>:<subtype>' }})
 * render a searchable dropdown of stored connection profiles of that catalog
 * type from the live nucleus-connectors-service.
 *
 * Uses the lib's generated {@code ConnectionControllerService.byRequirement(type,
 * subtype)} (GET /connections/by-requirement) — the catalog endpoint built for
 * matching a capability's connector requirement; returns {@link ConnectionSummaryDto}
 * (secrets-free). Mirrors {@code app-user-data-picker}: single-select; the form
 * value is the {@code connectionId} (what travels in the task payload →
 * ExecuteCommand.bindings), the full summary is emitted via {@code (selectionChange)}.
 */
@Component({
    selector: 'app-connection-picker',
    standalone: true,
    imports: [
        CommonModule, ReactiveFormsModule,
        MatFormFieldModule, MatInputModule, MatAutocompleteModule, MatIconModule, MatButtonModule,
    ],
    templateUrl: './connection-picker.component.html',
    styleUrl: './connection-picker.component.css',
})
export class ConnectionPickerComponent implements OnInit, OnChanges {

    /** Required. The connectors-service catalog type ("datasource" / "remote" / …). */
    @Input({ required: true }) type!: string;

    /** Optional subtype ("jdbc" / "ssh" / …) narrowing the catalog query. */
    @Input() subtype = '';

    @Input() label = '';
    /** Pre-selected connectionId — drives the initial display value. */
    @Input() value: string | null = null;
    @Input() placeholder = '';
    @Input() required = false;
    @Input() disabled = false;

    /** Full summary on selection (or null on clear). */
    @Output() selectionChange = new EventEmitter<ConnectionSummaryDto | null>();
    /** Bare connectionId, so callers can [(value)]-bind without unpacking. */
    @Output() valueChange = new EventEmitter<string | null>();

    private readonly api = inject(ConnectionControllerService);
    private readonly requestHandler = inject(RequestHandler);

    readonly inputControl = new FormControl<string | null>(null);
    rows: ConnectionSummaryDto[] = [];
    filtered: ConnectionSummaryDto[] = [];
    loading = false;
    loadError: string | null = null;

    ngOnInit(): void {
        this.fetch();
        this.inputControl.valueChanges.subscribe(v => {
            const needle = (v ?? '').toString().toLowerCase();
            this.filtered = needle
                ? this.rows.filter(r =>
                    (r.name ?? '').toLowerCase().includes(needle) ||
                    (r.connectionId ?? '').toLowerCase().includes(needle))
                : [...this.rows];
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['type'] && !changes['type'].firstChange) ||
            (changes['subtype'] && !changes['subtype'].firstChange)) {
            this.fetch();
        }
        if (changes['required'] || changes['disabled']) this.applyValidators();
        if (changes['value']) this.inputControl.setValue(this.value ?? '', { emitEvent: false });
    }

    private applyValidators(): void {
        if (this.required) this.inputControl.setValidators([Validators.required]);
        else this.inputControl.clearValidators();
        if (this.disabled) this.inputControl.disable({ emitEvent: false });
        else this.inputControl.enable({ emitEvent: false });
        this.inputControl.updateValueAndValidity({ emitEvent: false });
    }

    private fetch(): void {
        if (!this.type) { this.rows = []; this.filtered = []; return; }
        this.loading = true;
        this.loadError = null;
        this.requestHandler.handleRequest(
            this.api.byRequirement(this.type, this.subtype || undefined) as Observable<ConnectionSummaryDto[]>,
            {
                onStart: () => {},
                onSuccess: (list: ConnectionSummaryDto[]) => {
                    this.rows = (list ?? []).slice().sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
                    this.filtered = [...this.rows];
                    if (this.value) this.inputControl.setValue(this.value, { emitEvent: false });
                },
                onError: (err: string) => {
                    this.loadError = err || 'Failed to load connections.';
                    this.rows = [];
                    this.filtered = [];
                },
                onComplete: () => { this.loading = false; },
            },
            false,
        );
    }

    onOptionSelected(event: MatAutocompleteSelectedEvent): void {
        const id = event.option.value as string;
        const row = this.rows.find(r => r.connectionId === id) ?? null;
        this.value = id;
        this.selectionChange.emit(row);
        this.valueChange.emit(id);
    }

    onClear(): void {
        this.inputControl.setValue('');
        this.value = null;
        this.selectionChange.emit(null);
        this.valueChange.emit(null);
    }

    /** Show the friendly name for a selected id (the control value is the id). */
    displayFn = (id: string | null): string => {
        if (!id) return '';
        const r = this.rows.find(x => x.connectionId === id);
        return r?.name ?? id;
    };

    effectiveLabel(): string {
        return this.label || (this.subtype ? `${this.type}:${this.subtype}` : this.type);
    }
}
